import { memo } from 'react'
import { hideImageOnError } from '../../../hooks/useImageErrorHandler'

/**
 * EntityPopupHeader - Header section for entity popup with image and title
 * 
 * Features:
 * - Hero image with gradient overlay
 * - Entity name and category badge
 * - Clickable navigation for normal mode
 * - Fallback styling when no image available
 * 
 * @param {Object} props
 * @param {string} props.displayImageUrl - Image URL to display
 * @param {string} props.variant - 'normal' | 'entity'
 * @param {Object} props.entity - Entity data
 * @param {Function} props.onNavigate - Navigation handler (normal mode only)
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render popup header
 */
const EntityPopupHeader = memo(function EntityPopupHeader({
  displayImageUrl,
  variant,
  entity,
  onNavigate
}) {
  const isClickable = variant === 'normal'

  const handleInteraction = (e) => {
    if (!isClickable) return
    e.preventDefault()
    e.stopPropagation()
    onNavigate()
  }

  const handleKeyDown = (e) => {
    if (!isClickable) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onNavigate()
    }
  }

  return (
    <div className="relative">
      {displayImageUrl ? (
        <div 
          className={`relative h-16 rounded-t-xl overflow-hidden ${isClickable ? 'cursor-pointer hover:opacity-95 transition-opacity' : ''}`}
          onClick={isClickable ? handleInteraction : undefined}
          onTouchEnd={isClickable ? handleInteraction : undefined}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={isClickable ? handleKeyDown : undefined}
        >
          <img 
            src={variant === 'entity' ? displayImageUrl : (displayImageUrl.startsWith('http') ? displayImageUrl : `http://localhost:8081${displayImageUrl}`)} 
            alt={entity.name}
            className={`w-full h-full object-cover ${isClickable ? 'pointer-events-none' : ''}`}
            onError={hideImageOnError}
          />
          <div className={`absolute inset-0 bg-black/50 ${isClickable ? 'pointer-events-none' : ''}`} />
          <div className={`absolute bottom-1.5 left-3 right-3 ${isClickable ? 'pointer-events-none' : ''}`}>
            <h3 className="font-semibold text-sm text-white drop-shadow-lg leading-tight">
              {entity.name}
            </h3>
            {entity.category && (
              <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-[10px] rounded-full capitalize leading-tight">
                {entity.category.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="px-3 py-2.5 bg-primary/5 rounded-t-xl border-b border-gray-200/50">
          <h3 className="font-semibold text-sm text-gray-900">
            {entity.name}
          </h3>
          {entity.category && (
            <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] rounded-full capitalize font-medium leading-tight">
              {entity.category.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

export default EntityPopupHeader
